import { useCallback, useEffect, useRef } from 'react'

import { type OpenableControls } from '../../../generic-hooks/useOpenableControls/useOpenableControls'
import { gtmDataLayerPush } from '../../../gtm/gtmDataLayer'

const ERX_MODAL_TIMEOUT = 10_000

/**
 * Custom hook that sets a timeout to open the eRx modal after a specified delay.
 * The modal will only be opened if the user has interacted with the page first, triggered from useUserInteractionGate hook.
 * @param shouldErxModalBeShown - Flag indicating whether the eRx modal should be shown.
 * @param modalControls - Controls for opening and closing the modal.
 * @param setErxModalShown - Function to set the visibility of the eRx modal via localStorage.
 */
export const useOpenModal = (
  shouldErxModalBeShown: boolean,
  modalControls: OpenableControls,
  setErxModalShown: (value: boolean) => void,
): (() => void) => {
  const timeoutRef = useRef<number | null>(null)
  const timeoutCallback = useCallback(() => {
    timeoutRef.current = window.setTimeout(() => {
      gtmDataLayerPush({
        event: 'popin',
        popinName: '__eRxModal',
      })

      if (shouldErxModalBeShown) {
        setErxModalShown(true)
        modalControls.handleOpen()
      }
    }, ERX_MODAL_TIMEOUT)
  }, [modalControls, shouldErxModalBeShown, setErxModalShown])

  useEffect(() => {
    return (): void => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return timeoutCallback
}
